import { Autocomplete, TextField } from "@mui/material";
import { useState, useEffect } from "react";
import "../../assets/css/Organization/OrganizationDrawer.css";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { MdEmail, MdClose } from "react-icons/md";
import { FaPhoneAlt, FaEdit, FaTrashAlt, FaUserFriends, FaExternalLinkAlt } from "react-icons/fa";
import LeadsDynamic from "../Leads/LeadsDynamic";
import DealsDynamic from "../Deals/DealsDynamic";

export default function OrganizationDrawer({
  organization,
  isOpen,
  onClose,
  refreshOrganizations,
}) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [contacts, setContacts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [orgDetails, setOrgDetails] = useState(organization || {});

  const fetchOrgDetails = async () => {
    try {
      const res = await api.get(`/organization/${organization?.id}`);
      if (res?.data) {
        setOrgDetails((prev) => ({
          ...prev,
          ...res.data,
          deals: res.data.deals ?? prev?.deals ?? [],
          leads: res.data.leads ?? prev?.leads ?? [],
        }));
      }
    } catch (err) {
      console.error("Failed to load organization details", err);
    }
  };

  const fetchContactsAndLeads = async () => {
    try {
      const response = await api.get(
        `/contact/organization/${organization?.id}`
      );
      console.log("Fetched contacts data:", response.data);
      setContacts(response.data || []);
      console.log(organization?.leads, "organization");
    } catch (error) {
      console.error("Failed to load contacts/leads", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await api.get("/countries/");
      const sorted = (res.data || []).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setCountries(sorted);
      return sorted;
    } catch (error) {
      console.error("Failed loading countries", error);
      return [];
    }
  };

  const allLeads =
    (orgDetails?.leads || organization?.leads || []).map((lead) => ({
      ...lead,
      organization_name:
        lead.organization_name ||
        lead.organization?.name ||
        orgDetails?.name ||
        organization?.name ||
        "",
      contact_name:
        lead.contact_name ||
        `${lead.contact?.first_name || ""} ${lead.contact?.last_name || ""}`.trim(),
      contact_email: lead.contact_email || lead.contact?.email,
      contact_phone: lead.contact_phone || lead.contact?.phone,
    }));

  useEffect(() => {
    if (!organization?.id) return;

    setOrgDetails(organization);
    setFormData({
      ...organization,
    });

    setEditMode(false);
    setActiveTab("overview");

    fetchOrgDetails();
    fetchContactsAndLeads();
    fetchCountries();
  }, [organization]);

  if (!isOpen) return null;

  const handleStartEdit = async () => {
    setEditMode(true);
    let currentCountries = countries;
    if (!currentCountries || currentCountries.length === 0) {
      currentCountries = await fetchCountries();
    }

    const currentCountryName = formData.country || organization?.country;
    if (currentCountryName) {
      const countryObj = currentCountries.find(
        (c) => c.name === currentCountryName
      );
      if (countryObj?.iso2) {
        fetchStates(countryObj.iso2);
      }
    }
  };

  const fetchStates = async (countryCode) => {
    try {
      const res = await api.get(`/countries/${countryCode}/states`);
      const sortedStates = res.data.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setStates(sortedStates);
    } catch (error) {
      console.error("Failed loading states", error);
      setStates([]);
    }
  };

  const converteddeals =
    (orgDetails?.deals || organization?.deals || []).map((deal) => ({
      ...deal,
      organization_name:
        deal.organization_name ||
        deal.organization?.name ||
        orgDetails?.name ||
        organization?.name ||
        "",
      contact_name:
        deal.contact_name ||
        `${deal.contact?.first_name || ""} ${deal.contact?.last_name || ""}`.trim(),
      contact_email: deal.contact_email || deal.contact?.email,
      contact_phone: deal.contact_phone || deal.contact?.phone,
    }));

  const handleLeadClick = (lead) => {
    if (!lead?.id) return;
    if (onClose) onClose();
    navigate("/leads", {
      state: {
        openLeadId: lead.id,
      },
    });
  };

  const handleDealClick = (deal) => {
    if (!deal?.id) return;
    if (onClose) onClose();
    navigate("/deals", {
      state: {
        openDealId: deal.id,
      },
    });
  };

  console.log("Processed leads data:", allLeads);
  console.log("Processed deals data:", converteddeals);

  const MAX_LENGTH = 70;

  const isBlank = (value) => !value || value.trim() === "";

  const validate = () => {
    const newErrors = {};

    if (isBlank(formData.name)) {
      newErrors.name = "Organization name is required";
    } else if (formData.name.trim().length > MAX_LENGTH) {
      newErrors.name = `Maximum ${MAX_LENGTH} characters allowed`;
    }

    if (isBlank(formData.country)) {
      newErrors.country = "Country is required";
    }

    if (isBlank(formData.state)) {
      newErrors.state = "State is required";
    } else if (formData.state.trim().length > MAX_LENGTH) {
      newErrors.state = `Maximum ${MAX_LENGTH} characters allowed`;
    }

    if (isBlank(formData.status)) {
      newErrors.status = "Status is required";
    }

    if (isBlank(formData.address)) {
      newErrors.address = "Address is required";
    } else if (formData.address.trim().length > MAX_LENGTH) {
      newErrors.address = `Maximum ${MAX_LENGTH} characters allowed`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveOrganization = async () => {
    if (!validate()) return;
    try {
      await api.put(`/organization/${organization.id}`, formData);

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Organization updated successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      if (refreshOrganizations) {
        await refreshOrganizations();
      }

      onClose();
      navigate("/organizations", { replace: true });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Something went wrong",
        timer: 2000,
      });
    }
  };

  const deleteOrganization = async () => {
    const result = await Swal.fire({
      title: "Delete Organization?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/organization/${organization.id}`);

      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Organization removed successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      if (refreshOrganizations) {
        await refreshOrganizations();
      }

      onClose();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Could not delete organization",
      });
    }
  };

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join("");
  };

  const currentUser = JSON.parse(sessionStorage.getItem("user"));
  const role = currentUser?.role?.toLowerCase();

  const permissions = {
    "super admin": {
      edit: true,
      delete: true,
    },
    "sales manager": {
      edit: true,
      delete: false,
    },
    "sales executive": {
      edit: false,
      delete: false,
    },
  };

  const permission = permissions[role] || {
    edit: false,
    delete: false,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  console.log("Organization:", organization);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div
        className="organization-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="drawer-header-o">
          <div className="organization-header-left">
            <div className="organization-header-info">
              <div className="organization-avatar">
                {getInitials(formData?.name || organization?.name)}
              </div>

              <div className="organization-title-block">
                <h2>{organization?.name}</h2>

                <div className="organization-badges">
                  <span
                    className={`status-pill ${
                      organization?.status?.toLowerCase() === "active"
                        ? "active"
                        : "inactive"
                    }`}
                  >
                    <span className="status-dot"></span>
                    {organization?.status || "-"}
                  </span>

                  {organization?.country && (
                    <span className="country-badge">
                      {organization?.country}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="header-right">
            {!editMode && (
              <div className="header-icon-actions">
                {permission.edit && (
                  <button
                    aria-label="edit-organization"
                    className="icon-btn edit-btn"
                    onClick={handleStartEdit}
                    title="Edit Organization"
                  >
                    <FaEdit />
                  </button>
                )}

                {permission.delete && (
                  <button
                    aria-label="delete-organization"
                    className="icon-btn delete-btn"
                    onClick={deleteOrganization}
                    title="Delete Organization"
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
            )}
            <button
              aria-label="close-drawer"
              className="close-btn"
              onClick={onClose}
              title="Close Drawer"
            >
              <MdClose />
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="drawer-tabs">
          <button
            className={activeTab === "overview" ? "active-tab" : ""}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>

          <button
            className={activeTab === "leads" ? "active-tab" : ""}
            onClick={() => setActiveTab("leads")}
          >
            Leads
          </button>

          <button
            className={activeTab === "deals" ? "active-tab" : ""}
            onClick={() => setActiveTab("deals")}
          >
            Deals
          </button>
        </div>

        {/* ================= OVERVIEW TAB ================= */}
        {activeTab === "overview" && (
          <div className="drawer-overview-container">
            {/* OVERVIEW CONTENT (Clean, open, borderless) */}
            <div className="drawer-content">
              <div className="detail-grid">
                {editMode && (
                  <div>
                    <label>Organization Name</label>
                    <input
                      name="name"
                      value={formData.name || ""}
                      onChange={handleChange}
                      maxLength={MAX_LENGTH}
                      className={errors.name ? "input-error" : ""}
                      placeholder="Enter organization name"
                    />
                    {errors.name && (
                      <small className="error-text">{errors.name}</small>
                    )}
                  </div>
                )}

                <div>
                  <label>Website</label>
                  {editMode ? (
                    <>
                      <input
                        name="website"
                        value={formData.website || ""}
                        onChange={handleChange}
                        maxLength={MAX_LENGTH}
                        className={errors.website ? "input-error" : ""}
                        placeholder="https://example.com"
                      />
                      {errors.website && (
                        <small className="error-text">{errors.website}</small>
                      )}
                    </>
                  ) : (
                    <p>
                      {organization?.website ? (
                        <a
                          href={
                            organization.website.startsWith("http")
                              ? organization.website
                              : `https://${organization.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="website-link"
                        >
                          {organization.website}
                          <FaExternalLinkAlt className="external-icon" />
                        </a>
                      ) : (
                        "-"
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label>Status</label>
                  {editMode ? (
                    <select
                      name="status"
                      value={formData.status || ""}
                      onChange={handleChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  ) : (
                    <p>
                      <span
                        className={`status-pill ${
                          organization?.status?.toLowerCase() === "active"
                            ? "active"
                            : "inactive"
                        }`}
                      >
                        <span className="status-dot"></span>
                        {organization?.status || "-"}
                      </span>
                    </p>
                  )}
                </div>

                <div>
                  <label>Country</label>
                  {editMode ? (
                    <Autocomplete
                      options={countries}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option?.name || ""
                      }
                      value={
                        countries.find((c) => c.name === formData.country) ||
                        (formData.country ? { name: formData.country, iso2: "" } : null)
                      }
                      onChange={(event, newValue) => {
                        console.log("Selected country:", newValue);
                        const selectedName =
                          typeof newValue === "string" ? newValue : newValue?.name || "";
                        setFormData((prev) => ({
                          ...prev,
                          country: selectedName,
                          state: "",
                        }));
                        setStates([]);
                        if (newValue?.iso2) {
                          fetchStates(newValue.iso2);
                        }
                        setErrors((prev) => ({
                          ...prev,
                          country: "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search country..."
                          error={!!errors.country}
                        />
                      )}
                    />
                  ) : (
                    <p>{organization?.country || "-"}</p>
                  )}
                </div>

                <div>
                  <label>State</label>
                  {editMode ? (
                    <Autocomplete
                      options={states}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option?.name || ""
                      }
                      value={
                        states.find((s) => s.name === formData?.state) ||
                        (formData?.state ? { name: formData.state } : null)
                      }
                      onChange={(event, newValue) => {
                        const selectedName =
                          typeof newValue === "string" ? newValue : newValue?.name || "";
                        setFormData((prev) => ({
                          ...prev,
                          state: selectedName,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          state: "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search state..."
                          error={!!errors.state}
                        />
                      )}
                    />
                  ) : (
                    <p>{organization?.state || "-"}</p>
                  )}
                </div>

                <div className="full-width">
                  <label>Address</label>
                  {editMode ? (
                    <>
                      <textarea
                        name="address"
                        value={formData.address || ""}
                        onChange={handleChange}
                        placeholder="Enter full office/business address..."
                        className={errors.address ? "input-error" : ""}
                      />
                      {errors.address && (
                        <small className="error-text">{errors.address}</small>
                      )}
                    </>
                  ) : (
                    <p>{organization?.address || "-"}</p>
                  )}
                </div>

                <div>
                  <label>Created By</label>
                  <p>
                    {organization.created_by?.name ||
                      organization.created_by ||
                      "-"}
                  </p>
                </div>

                <div>
                  <label>Created Date</label>
                  <p>
                    {organization?.created_at
                      ? new Date(
                          organization.created_at
                        ).toLocaleDateString()
                      : "-"}
                  </p>
                </div>
              </div>

              {/* EDIT ACTIONS INSIDE OVERVIEW */}
              {editMode && (
                <div className="overview-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setFormData(organization);
                      setEditMode(false);
                      setErrors({});
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn-primary"
                    onClick={saveOrganization}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* CONTACTS SECTION */}
            <div className="drawer-contacts-section">
              <div className="section-header">
                <h5>
                  <div className="icon-circle blue">
                    <FaUserFriends />
                  </div>
                  Contacts ({contacts.length})
                </h5>
              </div>

              <div className="contacts-content">
                {contacts.length === 0 ? (
                  <div className="empty-contacts">
                    <p>No contacts associated with this organization.</p>
                  </div>
                ) : (
                  <div className="contacts-list">
                    {contacts.map((contact) => (
                      <div className="contact-card" key={contact.id}>
                        <div className="contact-avatar">
                          {contact.first_name?.charAt(0)}
                          {contact.last_name?.charAt(0)}
                        </div>

                        <div className="contact-info">
                          <h4>
                            {`${contact.first_name || ""} ${
                              contact.last_name || ""
                            }`.trim() || "Unnamed Contact"}
                          </h4>

                          {contact.email && (
                            <p>
                              <MdEmail className="contact-icon" />
                              <a href={`mailto:${contact.email}`}>
                                {contact.email}
                              </a>
                            </p>
                          )}

                          {contact.phone && (
                            <p>
                              <FaPhoneAlt className="contact-icon" />
                              <a href={`tel:${contact.phone}`}>
                                {contact.phone}
                              </a>
                            </p>
                          )}

                          {contact.designation && (
                            <span className="contact-role">
                              {contact.designation}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================= LEADS TAB ================= */}
        {activeTab === "leads" && (
          <div className="drawer-dynamic-tab-container">
            <LeadsDynamic
              leads={allLeads}
              title="Leads"
              onLeadClick={handleLeadClick}
              onFollowupSuccess={fetchOrgDetails}
            />
          </div>
        )}

        {/* ================= DEALS TAB ================= */}
        {activeTab === "deals" && (
          <div className="drawer-dynamic-tab-container">
            <DealsDynamic
              deals={converteddeals}
              title="Deals"
              onDealClick={handleDealClick}
              onFollowupSuccess={fetchOrgDetails}
            />
          </div>
        )}
      </div>
    </div>
  );
}